import { RefObject, useEffect, useState } from "react"
import { MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from "./constants"

export type CallLayoutMode = "desktop" | "tablet" | "mobile-portrait" | "mobile-landscape"

export type CallLayout = {
  mode: CallLayoutMode
  width: number
  height: number
  /** Anything narrower than desktop: header labels collapse to icons and the panel starts closed. */
  isCompact: boolean
  isMobile: boolean
  /**
   * How the workspace panel shares space with the video: docked beside it ("sidebar") on
   * desktop, tablet, and landscape-phone widths, covering it ("drawer") only on portrait phones.
   * A half-screen browser window on a laptop is a tablet width, and there the video must stay
   * in front; a landscape phone is wide enough for a narrow docked panel, as Daily Prebuilt does.
   */
  panelMode: "sidebar" | "drawer"
}

/** Landscape phones are wider than the mobile breakpoint but far too short to be treated as tablets. */
const MOBILE_LANDSCAPE_MAX_HEIGHT = 500

export const resolveCallLayoutMode = (width: number, height: number): CallLayoutMode => {
  if (width >= TABLET_BREAKPOINT) return "desktop"
  if (width > height && height < MOBILE_LANDSCAPE_MAX_HEIGHT) return "mobile-landscape"
  if (width >= MOBILE_BREAKPOINT) return "tablet"
  return height >= width ? "mobile-portrait" : "mobile-landscape"
}

const readWindowSize = () => ({
  width: typeof window === "undefined" ? 1280 : window.innerWidth || 1280,
  height: typeof window === "undefined" ? 800 : window.innerHeight || 800,
})

const buildLayout = (width: number, height: number, mode = resolveCallLayoutMode(width, height)): CallLayout => {
  const isMobile = mode === "mobile-portrait" || mode === "mobile-landscape"
  return {
    mode,
    width,
    height,
    isCompact: mode !== "desktop",
    isMobile,
    panelMode: mode === "mobile-portrait" ? "drawer" : "sidebar",
  }
}

/**
 * Tracks the call surface's own size (not just the viewport) so the stage, thumbnail rail,
 * and side panel can adapt to desktop, tablet, and both mobile orientations. Falls back to
 * window size where ResizeObserver is unavailable (tests, older browsers).
 */
export function useCallLayout(containerRef: RefObject<HTMLElement | null>): CallLayout {
  const [layout, setLayout] = useState<CallLayout>(() => {
    const { width, height } = readWindowSize()
    return buildLayout(width, height)
  })

  useEffect(() => {
    const element = containerRef.current
    const applySize = (width: number, height: number) => {
      setLayout((previous) => {
        // A height-only keyboard change while editing is not a rotation. Keep
        // the narrow portrait drawer; width changes still use real responsive
        // classification. No focus changes or keyboard-height guesses are made.
        const focused = document.activeElement
        const editing = Boolean(element?.contains(focused) && focused?.matches('textarea, input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="button"]):not([type="submit"]), [contenteditable="true"]'))
        const next = buildLayout(width, height, editing && previous.width === width ? previous.mode : undefined)
        if (previous.mode === next.mode && previous.width === next.width && previous.height === next.height) {
          return previous
        }
        return next
      })
    }
    const applyFromWindow = () => {
      const { width, height } = readWindowSize()
      applySize(width, height)
    }

    if (element && typeof ResizeObserver === "function") {
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (!entry) return
        const { width, height } = entry.contentRect
        if (width > 0 && height > 0) {
          applySize(Math.round(width), Math.round(height))
        } else {
          applyFromWindow()
        }
      })
      observer.observe(element)
      applyFromWindow()
      return () => observer.disconnect()
    }

    applyFromWindow()
    window.addEventListener("resize", applyFromWindow)
    window.addEventListener("orientationchange", applyFromWindow)
    return () => {
      window.removeEventListener("resize", applyFromWindow)
      window.removeEventListener("orientationchange", applyFromWindow)
    }
  }, [containerRef])

  return layout
}
